import {Component, OnInit, OnDestroy, ViewEncapsulation, ChangeDetectionStrategy, ChangeDetectorRef, ElementRef} from '@angular/core';
import {DatacenterV2} from "../../core/model/datacenter-v2";
import {environment} from "../../../environments/environment";
import {ActivatedRoute} from "@angular/router";
import {Clipboard} from "@angular/cdk/clipboard";
import {DialogService, DynamicDialogRef} from "primeng/dynamicdialog";
import {NewDataCenterPopupComponent} from "../new-data-center-popup/new-data-center-popup.component";
import * as datacenterActions from '../../core/ngrx/actions/datacenter.actions';
import * as availabilityZoneActions from '../../core/ngrx/actions/availability-zone.actions';
import {AvailabilityZoneHandler} from '../../core/service/availability-zone.handler';
import {Store} from "@ngrx/store";
import {Actions, ofType} from '@ngrx/effects';
import {filter, Subject, take, takeUntil, tap} from 'rxjs';
import _ from "lodash";
import {Popup} from "mapbox-gl";
import {UserMap} from "../../core/model/user-map";
import {MapConfigSelector} from "../../core/ngrx/selectors/map-config.selector";
import {UrlFormatterService} from "../../core/service/url-formatter.service";

@Component({
  selector: 'app-info-popup',
  templateUrl: './info-popup.component.html',
  styleUrl: './info-popup.component.scss',
  encapsulation: ViewEncapsulation.None,
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class InfoPopupComponent implements OnInit, OnDestroy {
  private _item: DatacenterV2 = null;
  public popup: Popup = null;
  private destroy$ = new Subject<void>();
  public showCopiedTooltip = false;

  get item(): DatacenterV2 {
    return this._item;
  }

  set item(value: DatacenterV2) {
    this._item = value;
    if (this.cdr) {
      this.cdr.detectChanges();
    }
  }

  constructor(private route: ActivatedRoute, private clipboard: Clipboard,
              public dialogService: DialogService, private readonly store: Store, private urlFormatterService: UrlFormatterService,
              private mapConfigSelector: MapConfigSelector,
              private availabilityZoneHandler: AvailabilityZoneHandler, private cdr: ChangeDetectorRef,
              private actions$: Actions) {
  }

  ngOnInit(): void {
    this.actions$.pipe(
      ofType(availabilityZoneActions.toggleAZVisibility),
      takeUntil(this.destroy$)
    ).subscribe((action) => this.popup.remove());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  modifyDatacenter(dataCenter: DatacenterV2) {
    this.popup.remove();
    this.openPopup({
      ...dataCenter,
      toUpdate: true
    }).onClose.subscribe(
      (modifiedData: DatacenterV2) => {
        if (modifiedData) {
          this.store.dispatch(
            datacenterActions.datacenterModified({datacenter: modifiedData})
          );
        }
      }
    );
  }

  removeDatacenter(dataCenter: DatacenterV2) {
    this.popup.remove();
    const dcToDelete = _.cloneDeep(dataCenter)
    dcToDelete.toDelete = true;
    dcToDelete.toUpdate = false;
    this.store.dispatch(
      datacenterActions.removeDatacenter({datacenter: dcToDelete})
    );
  }

  copyMarkerInfo(dataCenter: DatacenterV2) {
    this.mapConfigSelector
      .getUserMap()
      .pipe(
        filter((userMap: UserMap) => !!userMap),
        take(1),
        tap((userMap: UserMap) => {
          const toShare = `${environment.envUrl}/map-config/${this.urlFormatterService.formatUserIdForUrl(userMap.userId)}/${userMap._id}?lat=${dataCenter.lat}&lon=${dataCenter.lon}`;
          this.clipboard.copy(toShare);
        })
      )
      .subscribe();
  }

  copyIdToClipboard(id: string) {
    this.clipboard.copy(id);
    this.showCopiedTooltip = true;

    setTimeout(() => {
      this.showCopiedTooltip = false;
    }, 2000);
  }

  openPopup(dataCenter: DatacenterV2):
    DynamicDialogRef {
    return this.dialogService.open(NewDataCenterPopupComponent, {
      header: 'Data Center',
      width: '40%',
      data: {
        ...dataCenter
      },
      contentStyle: {overflow: 'auto'},
      baseZIndex: 10000,
      maximizable: false,
    });
  }

  /**
   * Check if the datacenter has an availability zone
   */
  hasAvailabilityZone(datacenter: DatacenterV2): boolean {
    return !!(datacenter.availabilityZoneId && datacenter.availabilityZoneId.trim() !== '');
  }

  /**
   * Get availability zone visibility for current item based on source presence
   */
  getAzVisibility(): boolean {
    if (!this._item) return false;

    const azId = this._item.auxId || this._item.id;
    return this.availabilityZoneHandler.isAZVisibleInSource(azId);
  }

  /**
   * Toggle availability zone visibility and close popup
   */
  toggleAvailabilityZoneVisibility(datacenter: DatacenterV2): void {
    if (!this.hasAvailabilityZone(datacenter)) return;

    const azId = datacenter.auxId || datacenter.id;
    const currentVisibility = this.availabilityZoneHandler.isAZVisibleInSource(azId);
    const newVisibility = !currentVisibility;

    this.availabilityZoneHandler.toggleAZVisibilityInMap(azId, newVisibility);
    this.popup.remove();
  }

}
